import { ArrowRight } from "lucide-react";

interface OptionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  colors: {
    bgFrom: string;
    bgTo: string;
    border: string;
    hoverFrom: string;
    hoverTo: string;
    featureDots: string[];
  };
  onClick: () => void;
  badgeIcon?: React.ReactNode;
}

const OptionCard = ({
  title,
  description,
  icon,
  features,
  colors,
  onClick,
  badgeIcon,
}: OptionCardProps) => (
  <button
    onClick={onClick}
    className={`group relative overflow-hidden p-8 bg-gradient-to-br ${colors.bgFrom} ${colors.bgTo} hover:${colors.hoverFrom} hover:${colors.hoverTo} rounded-2xl border ${colors.border} transition-all duration-300 hover:shadow-xl hover:-translate-y-1 transform`}
  >
    {/* Background Pattern */}
    <div
      className={`absolute inset-0 bg-gradient-to-br ${colors.hoverFrom} ${colors.hoverTo} opacity-10`}
    ></div>
    <div
      className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${colors.hoverFrom} ${colors.hoverTo} rounded-full opacity-20 blur-2xl`}
    ></div>

    <div className="relative flex flex-col items-center text-center space-y-4">
      {/* Icon */}
      <div className="relative">
        <div
          className={`w-16 h-16 bg-gradient-to-br ${colors.bgFrom.replace(
            "-50",
            "-500"
          )} ${colors.bgTo.replace(
            "-50",
            "-600"
          )} rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110 transform`}
        >
          {icon}
        </div>
        {badgeIcon && (
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100">
            {badgeIcon}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="space-y-2">
        <h3
          className={`text-lg font-bold text-gray-900 group-hover:${colors.bgFrom.replace(
            "-50",
            "-700"
          )} transition-colors`}
        >
          {title}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
      </div>

      {/* Features */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        {features.map((feature, index) => (
          <span key={index} className="flex items-center gap-1">
            <div
              className={`w-1.5 h-1.5 ${colors.featureDots[index]} rounded-full`}
            ></div>
            {feature}
          </span>
        ))}
      </div>

      {/* Arrow */}
      <div
        className={`flex items-center justify-center w-10 h-10 bg-white bg-opacity-80 rounded-full group-hover:${colors.bgFrom.replace(
          "-50",
          "-100"
        )} transition-all duration-300`}
      >
        <ArrowRight
          className={`w-5 h-5 text-gray-400 group-hover:${colors.bgFrom.replace(
            "-50",
            "-600"
          )} transition-all duration-300 transform group-hover:translate-x-0.5`}
        />
      </div>
    </div>
  </button>
);

export default OptionCard;
